/**
 * Global keyboard layer — DESIGN.md §13.3/§13.5, Long-Day Review workstream 5.
 *
 * The review found exactly one global shortcut in the product (⌘K) against a
 * spec that commits to E/D/S/C/Esc (DESIGN.md:1101), so this is the whole
 * table in one place (KEYS-1). Two rules keep it honest:
 *
 *  - **One flat table, no chords and no modes.** `g` then `i` is a mode, and a
 *    mode is a thing to remember; the palette does "go to Inbox" better.
 *  - **The table is the documentation.** `?` renders this array, so a shortcut
 *    that exists is listed and one that is listed exists.
 *
 * Mounted once, at the root, behind the auth guard.
 */
import { useCallback, useMemo } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { ShortcutHelp } from '@/components/shortcut-help';
import { TITLE_PRIORITY, routeTitle, useDocumentTitle } from '@/lib/document-title';
import { useHotkeys, type HotkeyBinding } from '@/lib/hotkeys';
import { emitUi } from '@/lib/ui-events';
import { newCardTarget, useCurrentProjectKey } from '@/lib/current-project';

/* ------------------------------------------------------------------------ */
/* DOM helpers — the hotkeys drive the same controls the mouse does           */
/* ------------------------------------------------------------------------ */

function click(selector: string): boolean {
  const target = document.querySelector<HTMLElement>(selector);
  if (!target) return false;
  target.click();
  return true;
}

/**
 * Focus (and where it makes sense, open) a field on the card panel. The card
 * panel marks its controls `data-hotkey="status|assignee|due|priority|
 * edit-description|comment"`; text fields are focused, everything else is
 * activated, because a Select the user has to press Enter on again is a
 * shortcut that saved nothing.
 */
function focusCardField(name: string): boolean {
  const panel = document.querySelector<HTMLElement>('[data-card-panel]');
  const target = panel?.querySelector<HTMLElement>(`[data-hotkey="${name}"]`);
  if (!target) return false;

  target.scrollIntoView({ block: 'nearest' });
  target.focus({ preventScroll: true });

  const tag = target.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || target.isContentEditable) return true;

  if (target.getAttribute('role') === 'combobox' || target.hasAttribute('aria-haspopup')) {
    // Radix triggers open on pointerdown or Enter — never on a synthetic
    // `click()`, which is why this dispatches the key instead.
    target.dispatchEvent(
      new KeyboardEvent('keydown', { key: 'Enter', bubbles: true, cancelable: true }),
    );
    return true;
  }

  target.click();
  return true;
}

/**
 * What actually opens the card inside a `data-task-row`: the row's own link
 * (KEYS-12 made rows real `<a href="/card/N">`), or an element the list marks
 * `data-task-open`. Focusing *that* rather than the row wrapper is what makes
 * Enter work without a handler of our own.
 */
function rowActivator(row: HTMLElement): HTMLElement | null {
  return row.querySelector<HTMLElement>('[data-task-open], a[href]');
}

function rowTarget(row: HTMLElement): HTMLElement {
  if (row.matches('a[href], button')) return row;
  return rowActivator(row) ?? row;
}

/**
 * `j`/`k` walk the rows of whatever list is on screen by moving real DOM
 * focus, so the focus ring is the highlight and Enter activates the row
 * natively. `data-row-focus` is left behind as a hook for lists that want to
 * style the current row more strongly.
 */
function moveRowFocus(delta: number): boolean {
  const rows = [...document.querySelectorAll<HTMLElement>('[data-task-row]')];
  if (rows.length === 0) return false;

  const active = document.activeElement;
  const current = rows.findIndex(
    (row) => row === active || (active instanceof Node && row.contains(active)),
  );
  const marked = rows.findIndex((row) => row.hasAttribute('data-row-focus'));
  const from = current >= 0 ? current : marked;
  const next = rows[Math.min(Math.max(from + delta, 0), rows.length - 1)] ?? rows[0];
  if (!next) return false;

  for (const row of rows) row.removeAttribute('data-row-focus');
  next.setAttribute('data-row-focus', '');

  const target = rowTarget(next);
  if (
    !target.hasAttribute('tabindex') &&
    !target.matches('a[href], button, input, select, textarea')
  ) {
    target.setAttribute('tabindex', '-1');
  }
  target.focus({ preventScroll: true });
  target.scrollIntoView({ block: 'nearest' });
  return true;
}

/* ------------------------------------------------------------------------ */

export function GlobalHotkeys(): JSX.Element {
  const navigate = useNavigate();
  const location = useRouterState({ select: (state) => state.location });
  const hasCardParam = (location.search as { card?: unknown }).card !== undefined;

  /**
   * Drop the `card` search param — the peek's own close path (§13.1), reached
   * from the root where the route's search type is not known, hence the cast.
   */
  const closePeek = useCallback((): void => {
    const anyNavigate = navigate as unknown as (options: {
      search: (previous: Record<string, unknown>) => Record<string, unknown>;
      replace?: boolean;
    }) => Promise<void>;
    void anyNavigate({
      search: (previous) => {
        const next = { ...previous };
        delete next['card'];
        return next;
      },
    });
  }, [navigate]);

  const projectKey = useCurrentProjectKey();

  const bindings = useMemo<HotkeyBinding[]>(
    () => [
      /* --- Anywhere ---------------------------------------------------- */
      {
        key: 'k',
        mod: true,
        group: 'Anywhere',
        description: 'Command palette — go anywhere, do anything',
        run: () => emitUi('palette', {}),
      },
      {
        key: '/',
        group: 'Anywhere',
        description: 'Search cards, projects and comments',
        run: () => emitUi('palette', {}),
      },
      {
        key: 'c',
        group: 'Anywhere',
        description: 'New card',
        run: () => emitUi('new-card', newCardTarget(projectKey)),
      },
      {
        key: '[',
        group: 'Anywhere',
        description: 'Collapse or expand the sidebar',
        run: () => click('[data-sidebar-toggle]'),
      },
      {
        key: '?',
        group: 'Anywhere',
        description: 'This list of shortcuts',
        run: () => emitUi('help'),
      },
      {
        key: 'Escape',
        group: 'Anywhere',
        description: 'Close the card, or clear the selection',
        run: () => {
          // Radix owns Esc inside its own dialogs and menus; this is for the
          // peek and the bulk bar, which are page furniture (KEYS-3, KEYS-18).
          if (hasCardParam) {
            closePeek();
            return true;
          }
          if (document.querySelectorAll('[data-bulk-selected]').length > 0) {
            return click('[data-bulk-clear]');
          }
          return false;
        },
      },

      /* --- Lists ------------------------------------------------------- */
      {
        key: 'j',
        group: 'Lists',
        description: 'Next card in the list',
        run: () => moveRowFocus(1),
      },
      {
        key: 'k',
        group: 'Lists',
        description: 'Previous card in the list',
        run: () => moveRowFocus(-1),
      },
      {
        key: 'Enter',
        group: 'Lists',
        description: 'Open the highlighted card',
        hidden: true,
        run: () => {
          const active = document.activeElement;
          if (!(active instanceof HTMLElement)) return false;
          const row = active.closest<HTMLElement>('[data-task-row]');
          // Anything natively activatable keeps its own Enter.
          if (!row || active.matches('a[href], button, input, select, textarea')) return false;
          const activator = rowActivator(row);
          if (!activator) return false;
          activator.click();
          return true;
        },
      },

      /* --- The open card ----------------------------------------------- */
      {
        key: 's',
        group: 'The open card',
        description: 'Status',
        run: () => focusCardField('status'),
      },
      {
        key: 'a',
        group: 'The open card',
        description: 'Assignee',
        run: () => focusCardField('assignee'),
      },
      {
        key: 'd',
        group: 'The open card',
        description: 'Due date',
        run: () => focusCardField('due'),
      },
      {
        key: 'p',
        group: 'The open card',
        description: 'Priority',
        run: () => focusCardField('priority'),
      },
      {
        key: 'e',
        group: 'The open card',
        description: 'Edit the description',
        run: () => focusCardField('edit-description'),
      },
      {
        key: 'm',
        group: 'The open card',
        description: 'Write a comment',
        run: () => focusCardField('comment'),
      },
    ],
    // `closePeek` only closes over `navigate`, which is stable per router.
    [projectKey, hasCardParam, closePeek],
  );

  useHotkeys(bindings);

  return (
    <>
      <RouteTitles />
      <ShortcutHelp bindings={bindings} />
    </>
  );
}

/**
 * Per-route document titles (SHELL-4, KEYS-13) for the routes that do not name
 * themselves. Claimed at the lowest priority, so an open card's `#12 Title`
 * wins and the route title comes back when the card closes.
 */
function RouteTitles(): null {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  useDocumentTitle(routeTitle(pathname), TITLE_PRIORITY.route);
  return null;
}
